export const meta = {
  id: '31-nature-and-clothing',
  order: 31,
  titleFi: 'Luonto ja vaatteet',
  titleEn: 'Nature and clothing',
  duolingoUnit: ['duo-36-nature', 'duo-37-clothing'],
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: [
    'davābagon',
    'jehikagon',
    'qēlos',
    'sōnar',
    'jelmior',
    'sōna',
    'valar',
    'morghūljagon',
    'jelmio',
    'jelmȳrmo',
    'jēda',
    'jēdar-2',
    'jēdar',
    'vȳs',
    'guēse',
    'parmon',
    'lopon',
    'vellaros',
    'mīsītsor',
    'jonevegon',
    'landis',
    'bērio',
    'greviapos',
    'ubēriros',
    'ezīmpty',
    'qībōños',
    'ziksanna',
    'ours:deky',
    'ours:āgavītsos',
    'ours:yrgiapos',
    'ours:igriapos',
    'ours:dekuriapos',
    'ours:āgatriapos',
    'ours:bōsūljagon',
    'ours:mībūljagon', 'ours:ōrbrurlion', 'ours:sambrenka', 'ours:lōrūljagon', 'ours:zōbrūljagon', 'ours:suvītsos', 'ours:daomenka', 'ours:ōrbruragon', 'ours:albilūljagon', 'ours:trūmūljagon', 'ours:drāñūljagon',
    'ours:mīsītsoqitta',
    'ours:volpikvos',
    'ours:sȳndriapos',
    'ours:landītsos',
    'ours:biādrilliapos',
    'ours:temperatura',
    'ours:uniformi',
  ],
  exampleIds: [
    'ex-86a2a27a15ea',
    'ex-f5509e734468',
    'ex-2203afa7e0c8',
    'got-s5e03-001',
    'ex-e76564b18328',
    'ex-bf9b1d5cf983',
    'got-s3e06-001',
    'ex-6f7358ee2e44',
    'ex-7963d3403004',
    'ex-359d7c3d5152',
    'ex-1d9a95c1415b',
    'ex-fa2a87cec2d2',
    'ex-1c6ea66c8e1e',
    'ex-e977d48f3dc1',
    'got-s4e06-001',
    'ex-31ccf215fc89',
    'hotd-s2e08-001',
    'ex-0dd1bcb54a11',
    'ex-e876f212b5b7',
    'ex-71e1ac5ac7e6',
    'ex-c45a0ac882e0',
    'ex-222d4a785b43',
    'ex-169aae75356b',
    'ex-69bcb80f5eaa',
    'ex-df025882c140',
    'ex-36ff1b79bd66',
    'ex-24b930a90623',
    'ex-88919989f2ea',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Nature',
    'https://wiki.languageinvention.com/index.php?title=jehikagon',
    'https://wiki.languageinvention.com/index.php?title=tegot',
    'https://wiki.languageinvention.com/index.php?title=Jēdar',
    'https://wiki.languageinvention.com/index.php?title=jelmȳrmo',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Reference_Grammar#Reanalyzed_Collectives',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Clothing',
    'https://wiki.languageinvention.com/index.php?title=vellaros',
    'https://wiki.languageinvention.com/index.php?title=landis',
    'https://wiki.languageinvention.com/index.php?title=mīsītsor',
    'https://wiki.languageinvention.com/index.php?title=jonevegon',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Number',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Conjunctive_lengthening',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['numbers', 'word-order', 'valence'],
  showLines: ['got-s5e03-001', 'got-s3e06-001', 'got-s4e06-001', 'hotd-s2e08-001'],
  pronunciation: { focus: 'y', words: ['jelmȳrmo', 'vȳs', 'ezīmpty', 'Zaldrījudirys', 'Mȳr'], lines: ['got-s5e03-001', 'got-s3e06-001', 'got-s4e06-001'] },
  letter: {
    fi: 'Kuvaile päivän sää ja mitä puit päällesi.',
    en: 'Describe today’s weather and what you put on.',
  },
  scene: 'got-s3e06-1',
}

# Luonto ja vaatteet

Kaksi sanastoltaan rikasta osiota, joita yhdistää yksi kysymys: mitä kieli laskee yhdeksi? Luonnon sanoissa vastaan tulevat persoonattomat verbit ja uudet kollektiivit, vaatteissa yhdet housut ja vaatteet kollektiivina.

## Luonto

Tällä oppitunnilla sataa, tähdet loistavat ja talvi on tulossa. Luontosanojen mukana tulee neljä kielioppiasiaa: verbit, joilla ei ole subjektia; pienet määreet, jotka rajaavat substantiivia kuin <Term id="relatiivilause">relatiivilause</Term> (suomen *joka*-lause; oikeat relatiivilauseet tulevat <LessonLink n={46}>oppitunnilla 46</LessonLink>); kaksi uutta kollektiivia; ja kolme sanaa, jotka näyttävät lähes samoilta mutta ovat eri sanoja.

## Sataa: persoonattomat verbit

Valyriassa tulee joskus vastaan **persoonaton verbi**. Tämän oppitunnin esimerkki on <Hv glossed>davābagon</Hv> ’sataa’. Sillä ei ole subjektia, ja se taipuu kuin subjekti olisi yksikön 3. persoona. Tässä suomesta on suoraa apua: *sataa* toimii täsmälleen samoin. <Hv glossed>Davābas</Hv> on ’sataa’, <Hv glossed>davābilza</Hv> ’tulee satamaan’.

<Pronounce word="davābagon" />

<Example id="ex-86a2a27a15ea" />

<Example id="ex-f5509e734468" />

Kaikki sääverbit eivät kuitenkaan ole persoonattomia. <Hv glossed>Jehikagon</Hv> ’loistaa, paistaa’ saa subjektin eli sen, mikä loistaa: <Hv glossed>vēzos</Hv> ’aurinko’ tai <Hv glossed>qēlos</Hv> ’tähti’. Suomessakin sanotaan *aurinko paistaa*, ei pelkkä *paistaa*. Sanakirjan mukaan <Hv>jehikagon</Hv> voi saada myös objektin (akkusatiivissa: se, mitä valaistaan) ja välineen (instrumentaalissa: se, millä valaistaan).

<Pronounce word="jehikagon" />

<Example id="ex-2203afa7e0c8" />

Punainen papitar rukoilee juuri näin: ’valaise meidät valollasi’. <Hv glossed>Īlōn</Hv> ’meidät’ on objekti, <Hv glossed>ōñoso</Hv> ’valolla’ instrumentaali.

<Line id="got-s5e03-001" />

<Example id="ex-e76564b18328" />

## Maassa oleva mies: määrittävät lausekkeet

Relatiivilauseita et ole vielä opetellut, mutta joskus pienikin lauseke voi määrittää substantiivia, ja silloin sanajärjestys ratkaisee. Vertaa:

<Interlinear hv="Val|e teg|ot ūnd|an." gloss="mies-ACC maa-LOC nähdä-PRF.1SG" fi="Näin miehen maassa." />

<Interlinear hv="Teg|ot val|e ūnd|an." gloss="maa-LOC mies-ACC nähdä-PRF.1SG" fi="Näin maassa olevan miehen." />

Englanniksi molemmat ovat *I saw the man on the ground*, mutta merkitykset eroavat. Ensimmäisessä mies voi olla maassa, mutta ei välttämättä: ehkä minä seisoin maassa ja näin miehen puussa. Toisessa miehen **täytyy** olla maassa. Maassa oleminen kuuluu siihen, kuka mies on, kun taas minä voin olla missä tahansa, vaikka puussa.

Suomi tekee saman eron, mutta tarvitsee siihen partisiipin: *maassa **olevan** miehen*. Valyria ei tarvitse sitä: kun <Hv glossed>tegot</Hv> ’maassa’ seisoo **suoraan substantiivin edellä**, se määrittää substantiivia kuin <Term id="relatiivilause">relatiivilause</Term>. Jos se seisoo substantiivin jäljessä, tulkinta on toinen.

Tällaiset lausekkeet eivät ole oikeita relatiivilauseita, koska niissä ei ole verbiä. Niiden aika määräytyy kokonaan päälauseen verbin mukaan, eikä niillä voi olla omaa subjektia tai objektia, kuten relatiivilauseilla voi.

<Note>
<Hv glossed>Tegot</Hv> on muodoltaan sanan <Hv glossed>tegon</Hv> ’maa’ datiivi ja lokatiivi (kolmannessa <Term id="deklinaatio">taivutusluokassa</Term> ne ovat samat). Sanastossa se on myös omana sananaan: lohikäärmeelle annettava käsky *Maahan!*
</Note>

## Kaksi uutta kollektiivia: talvi ja sää

Kollektiivi tarkoittaa jonkin asian koko joukkoa, ja joskus siitä tulee kokonaan uusi sana. Tämän oppitunnin kaksi kaunista esimerkkiä:

- <Hv glossed>sōna</Hv> ’lumi’ → <Hv glossed>sōnar</Hv> ’talvi’, kaikki lumi yhdessä,
- <Hv glossed>jelmio</Hv> ’tuuli’ → <Hv glossed>jelmior</Hv> ’sää’, kaikki tuulet yhdessä.

Suomessa *lumi* ja *talvi* ovat eri sanoja, eikä kumpikaan ole toisen monikko. Siksi tähän kuvaan kannattaa pysähtyä: valyrialaiselle talvi on kaikki lumi yhdessä. Muista, että kollektiivi saa verbin **yksikköön**:

<Example id="ex-bf9b1d5cf983" />

<Interlinear hv="Sōn|ar māzi|s." gloss="lumi-NOM.COL tulla-PRS.3SG" fi="Talvi on tulossa." />

<Hv l="sōnar">Sōnar</Hv> taipuu samoin kuin <Hv l="vala">valar</Hv> – sana, josta koko lukujärjestelmä sai alkunsa:

<Line id="got-s3e06-001" />

<Paradigm lemma="sōnar" numbers="col" />

<Compare l="sōnar" fi="talve|lle" hv="sōn|arta" label="allatiivi ~ datiivi" />

<Hv l="jelmior">Jelmior</Hv> taipuu toisin: perusmuodon *io* muuttuu muissa sijoissa <Hv>ȳ</Hv>:ksi. Akkusatiivi on <Hv l="jelmior">jelmȳri</Hv>, genetiivi <Hv l="jelmior">jelmȳro</Hv>.

<Paradigm lemma="jelmior" numbers="col" />

<Pronounce l="jelmior" word="jelmior" />

<Example id="ex-6f7358ee2e44" />

<Example id="ex-7963d3403004" />

Sanastossa on myös taipumaton adjektiivi <Hv glossed>jelmȳrmo</Hv> ’myrskyinen’. Muodoltaan se on sama kuin sanan <Hv glossed>jelmior</Hv> komitatiivi ’sään kanssa’. Pohjoisen äpärien sukunimi <Hv glossed>Sōnaro</Hv> ’Snow’ taas on muodoltaan sama kuin sanan <Hv l="sōnar">sōnar</Hv> genetiivi:

<Example id="ex-359d7c3d5152" />

## Jēda, jēdar ja jēdar

Kolme sanaa, jotka kannattaa pitää erillään:

- <Hv glossed>jēda</Hv> ’vuodenaika’ on kuusukuinen.
- Sen kollektiivi <Hv glossed>jēdar</Hv> on myös oma sanansa, ’vuosi, vuodet’. Tämä tuli vastaan jo lukusanojen yhteydessä.
- Vesisukuinen <Hv glossed>jēdar</Hv> on ’taivas’. Yhdennäköisyys ’vuoden’ kanssa on sattumaa.

Perusmuoto on sama, mutta taivutus ei. Suomalainen tuntee ilmiön: *kuusi* on sekä lukusana että puu, ja ero kuuluu heti taivutuksessa (*kuuden* ~ *kuusen*). Samoin valyriassa ’vuoden’ on <Hv glossed>jēdaro</Hv>, mutta ’taivaan’ <Hv>jēdro</Hv>:

<Compare l="jēdar-2" fi="kuude|n" hv="jēd|aro" label="genetiivi: ’vuoden’" />

<Compare fi="kuuse|n" hv="jēdr|o" label="genetiivi: ’taivaan’" />

<Paradigm lemma="jēdar-2" numbers="col" />

<Paradigm lemma="jēdar" numbers="sg" />

<Example id="ex-1d9a95c1415b" />

<Example id="ex-fa2a87cec2d2" />

<Example id="ex-1c6ea66c8e1e" />

<Example id="ex-e977d48f3dc1" />

## Daenerys Jelmāzmo

Daenerysin lisänimen *Stormborn* Peterson käänsi sanalla <Hv l="jelmāzma">Jelmāzmo</Hv>. Se on sanan <Hv glossed>jelmāzma</Hv> ’myrsky’ genetiivi, joten <Hv glossed>Daenerys Jelmāzmo</Hv> on kirjaimellisesti ’myrskyn Daenerys’.

<Compare l="jelmāzma" fi="myrsky|n" hv="jelmāzm|o" label="genetiivi ~ genetiivi" />

<Line id="got-s4e06-001" />

<Example id="ex-31ccf215fc89" />

Kun kuulet Daemonin sanovan, että talvi tulee, katso verbiä tarkkaan. Siinä on <Term id="etuliite">etuliite</Term> *a-*, jonka merkitys selviää <LessonLink n={33}>oppitunnilla 33</LessonLink>:

<Line id="hotd-s2e08-001" />

## Harjoituslauseita

<Practice hv="Tubī davābas." fi="Tänään sataa." />

<Practice hv="Vēzos jehiksa." fi="Aurinko paistaa." />

<Practice hv="Nāvār ābre urnen." fi="Näen järvellä olevan naisen." />

<Practice hv="Sōnar māzis, se jelmior qapa issa." fi="Talvi tulee, ja sää on kylmä." />

## Muistettavaksi

- <Hv glossed>Davābagon</Hv> ’sataa’ on persoonaton ja taipuu yksikön 3. persoonassa; <Hv glossed>jehikagon</Hv> ’loistaa’ saa subjektin.
- Määre suoraan substantiivin edellä rajaa sitä kuin relatiivilause: <Hv glossed>tegot vale</Hv> ’maassa olevan miehen’.
- <Hv glossed>Sōnar</Hv> ’talvi’ ja <Hv glossed>jelmior</Hv> ’sää’ ovat kollektiiveja ja saavat verbin yksikköön.
- <Hv glossed>Jēdar</Hv> ’vuosi’ (<Hv l="jēdar-2">jēdaro</Hv>) ja <Hv glossed>jēdar</Hv> ’taivas’ (<Hv>jēdro</Hv>) taipuvat eri tavoin.

## Vaatteet

Vaatteiden osa on lyhyt, mutta siinä on kaksi ansaa, joihin suomalainen astuu helposti: yhdet housut, jotka ovat valyriaksi yksikössä, ja vaatteet, jotka ovat kollektiivissa. Kummassakin kysymys on sama kuin luonnon sanoissa ja edellisillä oppitunneilla: **mitä kieli laskee yhdeksi?**

## Yhdet housut: vellaros

<Hv glossed>Vellaros</Hv> tarkoittaa ’housut’. Englannin *pants* ja *trousers* ovat monikossa, ja niin on suomen *housut*: me sanomme *yhdet housut*, *kahdet housut*. Valyriassa <Hv>vellaros</Hv> on **yksikkö**: se tarkoittaa yhtä kokonaista housuparia. Monikko <Hv>vellarossa</Hv> tarkoittaa useampia housupareja.

<Compare fi="housu|t" hv="vellar|os" label="suomen monikko ~ valyrian yksikkö" />

Kun käännät, katso siis lukua tarkasti: <Hv glossed>vellaros</Hv> on suomeksi *housut* (yhdet), <Hv glossed>vellarossa</Hv> *useat housut*. Suomen monikkosana ei kerro, montako paria on kyseessä, mutta valyria kertoo.

<Paradigm lemma="vellaros" numbers="sg,pl" />

<Pronounce word="vellaros" />

<Example id="ex-0dd1bcb54a11" />

Tässä <Hv>vellaros</Hv> on yksikön akkusatiivi (kolmannen <Term id="deklinaatio">taivutusluokan</Term> aurinkosukuisissa sanoissa se on sama kuin nominatiivi). Hattu on muodossa <Hv glossed>ziksannē</Hv>: lauseen viimeisen sanan pidentynyt loppuvokaali merkitsee ’ja’, kuten opit aivan kurssin alussa.

<Example id="ex-e876f212b5b7" />

<Example id="ex-71e1ac5ac7e6" />

Sama pätee kenkiin. Sanakirjan mukaan <Hv glossed>landis</Hv> on ’kenkä, saapas’, mutta yksikkönä myös ’kenkäpari’. Monikko <Hv>landissa</Hv> taas tarkoittaa useita erillisiä, parittomia kenkiä. Suomessa *kengät* ovat monikossa, vaikka puhumme yhdestä parista.

<Example id="ex-c45a0ac882e0" />

## Vaatteet: kollektiivi

Toinen ansa on <Hv glossed>mīsītsor</Hv> ’vaatteet’. Se on sanan <Hv glossed>mīsītsos</Hv> ’vaatekappale’ **kollektiivi**: kaikki vaatteet yhdessä, vaatetus. Kollektiivi on kuin monikko, mutta se viittaa jonkin koko joukkoon, ja joskus kollektiivista syntyy kokonaan uusi sana, kuten tässä.

Suomessa *vaatteet* on monikko, mutta meillä on myös sana *vaatetus*, joka on yksikössä ja tarkoittaa kokonaisuutta. Ajattele sanaa <Hv l="mīsītsor">mīsītsor</Hv> vaatetuksena, niin verbikin osuu oikeaan lukuun: kollektiivi saa verbin **yksikköön**.

<Example id="ex-222d4a785b43" />

<Interlinear hv="Taob|o mīsīts|or qub|ys issa." gloss="poika-GEN vaate-NOM.COL huono-SOL.NOM.SG olla-PRS.3SG" fi="Pojan vaatteet ovat huonot." />

Suomeksi verbi on monikossa (*ovat*), valyriaksi yksikössä (<Hv l="sagon">issa</Hv>). Myös adjektiivi <Hv fi="huono">qubys</Hv> on yksikössä, ja se on aurinkosukuinen, koska <Hv l="mīsītsor">mīsītsor</Hv> säilyttää kantasanansa suvun.

Kollektiiveilla on oma taivutuksensa, ja tärkein muistettava muoto on akkusatiivi <Hv l="mīsītsor">mīsītsori</Hv>.

<Paradigm lemma="mīsītsor" numbers="col" />

<Pronounce l="mīsītsor" word="mīsītsor" />

## Vaatesanat ja suku

Vaatesanojen suku vaihtelee, mutta sanan loppu kertoo paljon. Kolmannen <Term id="deklinaatio">taivutusluokan</Term> <Hv>-os</Hv>-sanat ovat aurinkosukuisia:

- <Hv glossed>vellaros</Hv> ’housut’, <Hv glossed>greviapos</Hv> ’vyö’, <Hv glossed>qībōños</Hv> ’viitta’, <Hv glossed>ubēriros</Hv> ’liivi’.

Kuusukuisia ovat <Hv glossed>bērio</Hv> ’tunika’, <Hv glossed>ezīmpty</Hv> ’sandaali’ ja <Hv glossed>ziksanna</Hv> ’hattu’. <Hv glossed>Landis</Hv> ’kenkä’ on viidennen <Term id="deklinaatio">taivutusluokan</Term> aurinkosukuinen sana.

Suvun takia adjektiivi taipuu eri vaatteiden kanssa eri tavoin. Tässä suomi ei auta lainkaan: meillä ei ole sukuja, ja adjektiivi taipuu vain sijassa ja luvussa.

<Example id="ex-169aae75356b" />

<Example id="ex-69bcb80f5eaa" />

<Example id="ex-df025882c140" />

<Example id="ex-36ff1b79bd66" />

<Example id="ex-24b930a90623" />

<Example id="ex-88919989f2ea" />

## Pukeutuminen: jonevegon

’Pitää yllään, käyttää (vaatetta)’ on valyriaksi <Hv glossed>jonevegon</Hv>. Se on johdettu verbistä <Hv glossed>nevegon</Hv> ’kantaa’, joten vaatetta kirjaimellisesti *kannetaan*. Suomessakin voi *kantaa* sormusta tai kruunua. Verbi on vokaalivartaloinen *e*-verbi:

<Paradigm lemma="jonevegon" numbers="sg,pl" filter="indicative.active.present" />

<Pronounce word="jonevegon" />

## Harjoituslauseita

<Practice hv="Taoba vellaros joneves." fi="Pojalla on housut jalassa." />

<Practice hv="Riña ziksanne joneves." fi="Tytöllä on hattu päässä." />

<Practice hv="Mīsītsori joneven." fi="Minulla on vaatteet päällä." />

<Practice hv="Ñuhys mīsītsor qubys issa." fi="Vaatteeni ovat huonot." />

## Muistettavaksi

- <Hv>Vellaros</Hv> on yksikkö: yhdet housut. <Hv>Vellarossa</Hv> tarkoittaa useita housuja.
- <Hv>Landis</Hv> voi yksikössä tarkoittaa kenkäparia.
- <Hv glossed>Mīsītsor</Hv> ’vaatteet’ on kollektiivi: verbi yksikköön, akkusatiivi <Hv l="mīsītsor">mīsītsori</Hv>.
- <Hv>-os</Hv>-loppuiset vaatesanat ovat aurinkosukuisia, ja adjektiivi mukautuu sukuun.

## Sanat

<Vocab />

<Check />
